/*
 * CG Price Notice — open/close + bilingual toggle for the price-adjustment modal.
 *
 * - Once per browser session via sessionStorage (key below). Set on every close
 *   (button / backdrop / ESC). Reappears on a new session.
 * - Theme editor (Shopify.designMode): always open, never persist a dismissal,
 *   and re-open / re-wire on section select & reload.
 * - Language swap is CSS-driven off [data-active-lang]; JS just flips it.
 *
 * Loaded with `defer`, so the DOM is parsed when this runs.
 */
(function () {
  'use strict';

  const SS_KEY = 'cg-price-notice-dismissed';
  const OPEN_DELAY_MS = 2500; // storefront: open ~2.5s after load, not immediately

  const getDialog = () => document.querySelector('[data-cg-price-notice]');

  const inDesignMode = (dialog) =>
    (dialog && dialog.dataset.designMode === 'true') ||
    (window.Shopify && window.Shopify.designMode) ||
    false;

  const lockScroll = (locked) => {
    document.documentElement.style.overflow = locked ? 'hidden' : '';
  };

  const openModal = (dialog) => {
    if (!dialog || !dialog.isConnected || typeof dialog.showModal !== 'function') return;
    if (dialog.open) return;
    try {
      dialog.showModal();
      lockScroll(true);
      // Move focus off the first toggle button so no control shows a focus ring on open.
      const card = dialog.querySelector('.cg-price-notice__card');
      if (card) card.focus();
    } catch (e) {
      /* showModal can throw if already open or detached — ignore */
    }
  };

  const closeModal = (dialog, persist) => {
    if (!dialog) return;
    if (dialog.open) dialog.close();
    lockScroll(false);
    if (persist) {
      try {
        sessionStorage.setItem(SS_KEY, '1');
      } catch (e) {
        /* Safari private mode etc. — best effort */
      }
    }
  };

  const setLang = (dialog, lang) => {
    dialog.dataset.activeLang = lang;
    dialog.querySelectorAll('.cg-price-notice__lang-btn').forEach((btn) => {
      btn.setAttribute('aria-pressed', String(btn.dataset.lang === lang));
    });
  };

  // Bind listeners to a specific dialog node. Safe to call again on a freshly
  // re-rendered node in the theme editor (old node + its listeners are discarded).
  const wire = (dialog) => {
    const persist = !inDesignMode(dialog);

    dialog.querySelectorAll('.cg-price-notice__lang-btn').forEach((btn) => {
      btn.addEventListener('click', () => setLang(dialog, btn.dataset.lang));
    });

    const closeBtn = dialog.querySelector('[data-cg-close]');
    if (closeBtn) {
      closeBtn.addEventListener('click', () => closeModal(dialog, persist));
    }

    // ESC: <dialog> fires 'cancel' then closes — intercept so we run our own
    // close (and persist the dismissal) consistently. (Backdrop click does NOT
    // close — closing only via the Close button or ESC, by design.)
    dialog.addEventListener('cancel', (e) => {
      e.preventDefault();
      closeModal(dialog, persist);
    });
  };

  // Returns our dialog if the editor event belongs to this section, else null.
  // Matching by container containment is robust to section-group id formats.
  const dialogFromEvent = (e) => {
    if (e && e.target && typeof e.target.querySelector === 'function') {
      return e.target.querySelector('[data-cg-price-notice]');
    }
    return null;
  };

  const bindDesignModeListeners = () => {
    if (window.__cgPriceNoticeDesignBound) return;
    window.__cgPriceNoticeDesignBound = true;

    document.addEventListener('shopify:section:select', (e) => {
      const d = dialogFromEvent(e);
      if (d) openModal(d);
    });
    document.addEventListener('shopify:section:load', (e) => {
      const d = dialogFromEvent(e);
      if (d) {
        wire(d); // the section HTML (and dialog) was replaced — re-bind it
        openModal(d);
      }
    });
    document.addEventListener('shopify:section:deselect', (e) => {
      const d = dialogFromEvent(e);
      if (d) closeModal(d, false); // never persist in the editor
    });
  };

  const init = () => {
    const dialog = getDialog();
    if (!dialog) return;

    wire(dialog);

    if (inDesignMode(dialog)) {
      openModal(dialog);
      bindDesignModeListeners();
      return;
    }

    let dismissed = null;
    try {
      dismissed = sessionStorage.getItem(SS_KEY);
    } catch (e) {
      dismissed = null; // treat unreadable storage as "not dismissed"
    }
    if (!dismissed) window.setTimeout(() => openModal(dialog), OPEN_DELAY_MS);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
